import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import keyboardOnlyOutlines from '@moxy/keyboard-only-outlines'
import Image from 'next/image'
import { Icon } from '@theneatcompany/nui'
import { install } from 'resize-observer'

import useScope from '@/hooks/useScope'
import useAuth from '@/hooks/useAuth'
import { getMessage } from '@/utils/messages'
import scanSnapLogo from '@/assets/scan-snap.png'
import { INTERNAL_QUERY_PARAMS, SSO_SCOPE, APP_NAME } from '@/constants'
import useRoutes from '@/hooks/useRoutes'
import '@/styles/globals.scss'
import '@/utils/rollbar'

const App = dynamic(() => import('@/components/App'), { ssr: false })

const getHeaderImageProps = (ssoScope) => {
  switch (ssoScope) {
    case SSO_SCOPE.GOOGLE:
      return {
        src: '/neat-google.svg',
        width: 231,
        height: 44,
      }
    case SSO_SCOPE.INTUIT:
      return {
        src: '/neat-intuit.svg',
        width: 229,
        height: 39,
      }
    case SSO_SCOPE.APPLE:
      return {
        src: '/neat-apple.svg',
        width: 210,
        height: 39,
      }
    default:
      return {
        src: '/Neat-Logo-2021.svg',
        width: 145,
        height: 38,
      }
  }
}

export default function MyApp({ Component, pageProps }) {
  const { scopelessPaths, isScopeValid } = useScope()
  const router = useRouter()
  const routes = useRoutes()
  const { isValid } = useAuth()

  const [errorId, setErrorId] = useState(null)

  const isOnScopelessPage = scopelessPaths.includes(router.pathname)
  const isOnSsoLoginPage = router.pathname === routes.ssoLogin.path
  const ssoScope = router.query[INTERNAL_QUERY_PARAMS.ssoScope]
  const isOnScanSnapPage = router.pathname.includes('scansnap')

  useEffect(() => {
    if (!router.isReady || isOnScopelessPage) {
      return
    }

    if (!isScopeValid) {
      setErrorId('invalidScope')
      return
    }

    if (!isValid) {
      setErrorId('missingAuthParam')
    }
  }, [router.isReady, isValid, isOnScopelessPage])

  useEffect(() => {
    const fn = keyboardOnlyOutlines()
    if (!window.ResizeObserver) install()

    const boldText = 'font-weight: bold'
    const normalText = 'font-weight: normal'
     
    console.log(`%cWelcome to ${APP_NAME}!
    \n%cRelease Date: %c${process.env.releaseDate}
    \n%cTag: %c${process.env.NEXT_PUBLIC_BUILD_TAG}
    \n%cEnvironment: %c${process.env.NEXT_PUBLIC_ENV}
    \n%cCommit: %c${process.env.NEXT_PUBLIC_BUILD_COMMIT || 'not available'}`,
    boldText,
    boldText, normalText,
    boldText, normalText,
    boldText, normalText,
    boldText, normalText)

    return fn
  }, [])

  const renderLogo = useCallback((isError) => {
    if (!router.isReady) {
      return null
    }
    // @TODO create a combined image or svg instead?
    return isOnScanSnapPage ? (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Image src="/Neat-Logo-2021.svg" width={145} height={38} style={{ marginRight: '4px' }} alt="Neat logo" />
        <Icon name="add" fill="primary" />
        <Image width={70} height={56} src={scanSnapLogo} style={{ marginLeft: '-8px' }} alt="ScanSnap logo" />
      </div>
    ) : <Image priority={!!isError || router.pathname === routes.scanSnapAccounts.path} {...getHeaderImageProps(isOnSsoLoginPage ? ssoScope : undefined)} alt="Neat logo" />
  }, [router.isReady, ssoScope, isOnSsoLoginPage, isOnScanSnapPage])

  return (
    <>
      <Head>
        <title>Neat</title>
        <link rel="shortcut icon" href="/favicon.ico" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" />
      </Head>
      <App layoutProps={{ ...Component.layoutProps, error: errorId ? getMessage(`errors.${errorId}`) : undefined, renderLogo }}>
        {router.isReady && <Component {...pageProps} />}
      </App>
    </>
  )
}

MyApp.propTypes = {
  Component: PropTypes.func.isRequired,
  pageProps: PropTypes.shape({}).isRequired,
}
